import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PaymentIcon from '@mui/icons-material/Payment';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getOgrenciTaksitPlan,
  createTaksitPlan,
  odeTaksit
} from '../../../services/api/ogrenciService';
import { IndirimDetayModal, OgrenciResponseModal, OgrenciTaksitDetayModal, TaksitDetayModal, TaksitPlanRequestModal } from '../../../types/modals';

interface TaksitPlanDialogModalProps {
  open: boolean;
  onClose: () => void;
  ogrenci: OgrenciResponseModal;
  sezon: number;
  kurumKodu: string;
}

const odemeTurleri = ['Nakit', 'Kredi Kartı', 'Havale', 'EFT', 'Çek', 'Senet'];
const indirimTurleri = ['Kardeş', 'Öğretmen', 'Personel', 'Başarı', 'Diğer-1', 'Diğer-2'];

const TaksitPlanDialogModal: React.FC<TaksitPlanDialogModalProps> = ({
  open,
  onClose,
  ogrenci,
  sezon,
  kurumKodu
}) => {
  const queryClient = useQueryClient();

  // Taksit planı oluşturma için state'ler
  const [listeFiyati, setListeFiyati] = useState<number>(0);
  const [indirimTutari, setIndirimTutari] = useState<number>(0);
  const [netUcret, setNetUcret] = useState<number>(0);
  const [pesinatTutari, setPesinatTutari] = useState<number>(0);
  const [taksitlendirilecekMiktar, setTaksitlendirilecekMiktar] = useState<number>(0);
  const [taksitSayisi, setTaksitSayisi] = useState<number>(1);
  const [pesinatTarihi, setPesinatTarihi] = useState<string>(new Date().toISOString().split('T')[0]);
  const [taksitDetaylari, setTaksitDetaylari] = useState<TaksitDetayModal[]>([]);
  const [indirimDetaylari, setIndirimDetaylari] = useState<IndirimDetayModal[]>([]);

  // Taksit ödeme için state'ler
  const [selectedTaksit, setSelectedTaksit] = useState<OgrenciTaksitDetayModal | null>(null);
  const [odemeTutari, setOdemeTutari] = useState<number>(0);
  const [odemeTuru, setOdemeTuru] = useState<string>('Nakit');
  const [odemeAciklama, setOdemeAciklama] = useState<string>('EĞİTİM');
  const [showOdemeForm, setShowOdemeForm] = useState<boolean>(false);
  const [showTaksitPlanForm, setShowTaksitPlanForm] = useState<boolean>(false);

  // Taksit planını getirme query'si
  const {
    data: taksitPlan,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['ogrenciTaksitPlan', ogrenci.recNo, sezon],
    queryFn: async () => {
      return await getOgrenciTaksitPlan(
        kurumKodu,
        parseInt(ogrenci.recNo),
        sezon
      );
    },
    enabled: open && !!ogrenci.recNo,
    retry: 0,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity
  });

  // Taksit planı oluşturma mutation'ı
  const createTaksitPlanMutation = useMutation({
    mutationFn: (newTaksitPlan: TaksitPlanRequestModal) => {
      return createTaksitPlan(newTaksitPlan);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ogrenciTaksitPlan', ogrenci.recNo, sezon] });
      resetTaksitPlanForm();
      setShowTaksitPlanForm(false);
      refetch();
    },
    retry: 0,
  });

  // Taksit ödeme mutation'ı
  const odeTaksitMutation = useMutation({
    mutationFn: (params: { taksitId: number, tutar: number, odemeTuru: string, aciklama: string }) => {
      return odeTaksit(
        kurumKodu,
        params.taksitId,
        params.tutar,
        params.odemeTuru,
        params.aciklama
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ogrenciTaksitPlan', ogrenci.recNo, sezon] });
      resetOdemeForm();
      setShowOdemeForm(false);
      refetch();
    },
    retry: 0,
  });

  // Taksit detaylarını hesaplama
  useEffect(() => {
    if (taksitSayisi <= 0 || taksitlendirilecekMiktar <= 0) {
      setTaksitDetaylari([]);
      return;
    }

    const taksitTutari = parseFloat((taksitlendirilecekMiktar / taksitSayisi).toFixed(2));
    const pesinatDate = new Date(pesinatTarihi);

    const yeniTaksitler: TaksitDetayModal[] = [];

    for (let i = 0; i < taksitSayisi; i++) {
      const vadeTarihi = new Date(pesinatDate);
      vadeTarihi.setMonth(vadeTarihi.getMonth() + i);

      yeniTaksitler.push({
        taksitNo: i + 1,
        vadeTarihi: vadeTarihi.toISOString().split('T')[0],
        tutar: taksitTutari,
        borcTuru: 'Nakit'
      });
    }

    // Son taksitte küsurat farkını ekle
    const toplamTaksitTutari = taksitTutari * taksitSayisi;
    const fark = taksitlendirilecekMiktar - toplamTaksitTutari;

    if (fark !== 0 && yeniTaksitler.length > 0) {
      yeniTaksitler[yeniTaksitler.length - 1].tutar = parseFloat((yeniTaksitler[yeniTaksitler.length - 1].tutar + fark).toFixed(2));
    }

    setTaksitDetaylari(yeniTaksitler);
  }, [taksitSayisi, taksitlendirilecekMiktar, pesinatTarihi]);

  // Liste fiyatı, indirim ve net ücret değişikliklerini izleme
  useEffect(() => {
    const yeniNetUcret = listeFiyati - indirimTutari;
    setNetUcret(yeniNetUcret > 0 ? yeniNetUcret : 0);
  }, [listeFiyati, indirimTutari]);

  // Peşinat ve taksitlendirilecek miktar değişikliklerini izleme
  useEffect(() => {
    const yeniTaksitlendirilecekMiktar = netUcret - pesinatTutari;
    setTaksitlendirilecekMiktar(yeniTaksitlendirilecekMiktar > 0 ? yeniTaksitlendirilecekMiktar : 0);
  }, [netUcret, pesinatTutari]);

  // Form resetleme
  const resetTaksitPlanForm = () => {
    setListeFiyati(0);
    setIndirimTutari(0);
    setNetUcret(0);
    setPesinatTutari(0);
    setTaksitlendirilecekMiktar(0);
    setTaksitSayisi(1);
    setPesinatTarihi(new Date().toISOString().split('T')[0]);
    setTaksitDetaylari([]);
    setIndirimDetaylari([]);
  };

  const resetOdemeForm = () => {
    setSelectedTaksit(null);
    setOdemeTutari(0);
    setOdemeTuru('Nakit');
    setOdemeAciklama('EĞİTİM');
  };

  // Taksit planı oluşturma
  const handleCreateTaksitPlan = () => {
    if (listeFiyati <= 0) {
      alert('Lütfen geçerli bir liste fiyatı giriniz.');
      return;
    }

    if (taksitSayisi <= 0) {
      alert('Lütfen geçerli bir taksit sayısı giriniz.');
      return;
    }

    const taksitPlanRequest: TaksitPlanRequestModal = {
      kurumKodu: kurumKodu,
      ogrenciId: parseInt(ogrenci.recNo),
      listeFiyati,
      indirimTutari,
      netUcret,
      pesinatTutari,
      taksitlendirilecekMiktar,
      taksitSayisi,
      pesinatTarihi,
      taksitDetaylari,
      indirimDetaylari,
      sezon
    };

    createTaksitPlanMutation.mutate(taksitPlanRequest);
  };

  // Taksit ödeme
  const handleOdeTaksit = () => {
    if (!selectedTaksit) {
      alert('Lütfen bir taksit seçiniz.');
      return;
    }

    if (odemeTutari <= 0) {
      alert('Lütfen geçerli bir ödeme tutarı giriniz.');
      return;
    }

    odeTaksitMutation.mutate({
      taksitId: selectedTaksit.recNo,
      tutar: odemeTutari,
      odemeTuru,
      aciklama: odemeAciklama
    });
  };

  // Taksit seçme
  const handleSelectTaksit = (taksit: OgrenciTaksitDetayModal) => {
    setSelectedTaksit(taksit);
    setOdemeTutari(taksit.tutar - taksit.odenenTutar);
    setShowOdemeForm(true);
  };

  // İndirim ekleme
  const handleAddIndirim = () => {
    const yeniIndirim: IndirimDetayModal = {
      indirimTuru: 'Diğer-1',
      oran: 0,
      tutar: 0
    };

    setIndirimDetaylari([...indirimDetaylari, yeniIndirim]);
  };

  // İndirim güncelleme
  const handleIndirimChange = (index: number, field: keyof IndirimDetayModal, value: string | number) => {
    const updatedIndirimler = [...indirimDetaylari];

    if (field === 'indirimTuru') {
      updatedIndirimler[index].indirimTuru = value as string;
    } else if (field === 'oran') {
      const oran = parseFloat(value as string);
      updatedIndirimler[index].oran = oran;
      updatedIndirimler[index].tutar = (listeFiyati * oran) / 100;
    } else if (field === 'tutar') {
      const tutar = parseFloat(value as string);
      updatedIndirimler[index].tutar = tutar;
      updatedIndirimler[index].oran = listeFiyati > 0 ? (tutar / listeFiyati) * 100 : 0;
    }

    setIndirimDetaylari(updatedIndirimler);

    // Toplam indirim tutarını hesapla
    const toplamIndirim = updatedIndirimler.reduce((sum, indirim) => sum + indirim.tutar, 0);
    setIndirimTutari(toplamIndirim);
  };

  // İndirim silme
  const handleRemoveIndirim = (index: number) => {
    const updatedIndirimler = [...indirimDetaylari];
    updatedIndirimler.splice(index, 1);
    setIndirimDetaylari(updatedIndirimler);

    // Toplam indirim tutarını hesapla
    const toplamIndirim = updatedIndirimler.reduce((sum, indirim) => sum + indirim.tutar, 0);
    setIndirimTutari(toplamIndirim);
  };

  // Manuel yeniden yükleme işlevi
  const handleManualRefresh = () => {
    refetch();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {ogrenci.adiSoyadi} - Taksit Planı
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Öğrenci Bilgileri */}
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle2" color="text.secondary">Öğrenci No</Typography>
              <Typography variant="body1">{ogrenci.no}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle2" color="text.secondary">Sınıf</Typography>
              <Typography variant="body1">{ogrenci.sinif}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle2" color="text.secondary">Kayıt Tarihi</Typography>
              <Typography variant="body1">{ogrenci.kayitTarihi}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle2" color="text.secondary">Sezon</Typography>
              <Typography variant="body1">{sezon}</Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Taksit Planı Oluşturma Butonu */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Taksit Planı</Typography>
          <Box>
            {error && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleManualRefresh}
                sx={{ mr: 1 }}
                size="small"
              >
                Yenile
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setShowTaksitPlanForm(!showTaksitPlanForm)}
            >
              {showTaksitPlanForm ? 'İptal' : 'Yeni Taksit Planı Oluştur'}
            </Button>
          </Box>
        </Box>

        {/* Taksit Planı Oluşturma Formu */}
        {showTaksitPlanForm && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
            <Typography variant="subtitle1" gutterBottom>Yeni Taksit Planı</Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Liste Fiyatı (₺)"
                  type="number"
                  fullWidth
                  value={listeFiyati}
                  onChange={(e) => setListeFiyati(parseFloat(e.target.value) || 0)}
                  size="small"
                  InputProps={{ inputProps: { min: 0, step: "0.01" } }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="İndirim Tutarı (₺)"
                  type="number"
                  fullWidth
                  value={indirimTutari}
                  onChange={(e) => setIndirimTutari(parseFloat(e.target.value) || 0)}
                  size="small"
                  InputProps={{ inputProps: { min: 0, step: "0.01" } }}
                  required
                  disabled={indirimDetaylari.length > 0}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Net Ücret (₺)"
                  type="number"
                  fullWidth
                  value={netUcret}
                  disabled
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">İndirimler</Typography>
                  <Button
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleAddIndirim}
                  >
                    İndirim Ekle
                  </Button>
                </Box>

                {indirimDetaylari.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>İndirim Türü</TableCell>
                          <TableCell>Oran (%)</TableCell>
                          <TableCell>Tutar (₺)</TableCell>
                          <TableCell>İşlem</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {indirimDetaylari.map((indirim, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <FormControl fullWidth size="small">
                                <Select
                                  value={indirim.indirimTuru}
                                  onChange={(e) => handleIndirimChange(index, 'indirimTuru', e.target.value)}
                                >
                                  {indirimTurleri.map((tur) => (
                                    <MenuItem key={tur} value={tur}>{tur}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                size="small"
                                value={indirim.oran}
                                onChange={(e) => handleIndirimChange(index, 'oran', e.target.value)}
                                InputProps={{ inputProps: { min: 0, max: 100, step: "0.01" } }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                size="small"
                                value={indirim.tutar}
                                onChange={(e) => handleIndirimChange(index, 'tutar', e.target.value)}
                                InputProps={{ inputProps: { min: 0, step: "0.01" } }}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveIndirim(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Henüz indirim eklenmedi.
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Peşinat Tutarı (₺)"
                  type="number"
                  fullWidth
                  value={pesinatTutari}
                  onChange={(e) => setPesinatTutari(parseFloat(e.target.value) || 0)}
                  size="small"
                  InputProps={{ inputProps: { min: 0, max: netUcret, step: "0.01" } }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Taksitlendirilecek Miktar (₺)"
                  type="number"
                  fullWidth
                  value={taksitlendirilecekMiktar}
                  disabled
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Taksit Sayısı"
                  type="number"
                  fullWidth
                  value={taksitSayisi}
                  onChange={(e) => setTaksitSayisi(parseInt(e.target.value) || 0)}
                  size="small"
                  InputProps={{ inputProps: { min: 1, max: 36, step: "1" } }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Peşinat Tarihi"
                  type="date"
                  fullWidth
                  value={pesinatTarihi}
                  onChange={(e) => setPesinatTarihi(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>Taksit Detayları</Typography>

            {taksitDetaylari.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Taksit No</TableCell>
                      <TableCell>Vade Tarihi</TableCell>
                      <TableCell>Tutar (₺)</TableCell>
                      <TableCell>Ödeme Türü</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {taksitDetaylari.map((taksit, index) => (
                      <TableRow key={index}>
                        <TableCell>{taksit.taksitNo}. Taksit</TableCell>
                        <TableCell>
                          <TextField
                            type="date"
                            size="small"
                            value={taksit.vadeTarihi}
                            onChange={(e) => {
                              const updatedTaksitler = [...taksitDetaylari];
                              updatedTaksitler[index].vadeTarihi = e.target.value;
                              setTaksitDetaylari(updatedTaksitler);
                            }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={taksit.tutar}
                            onChange={(e) => {
                              const updatedTaksitler = [...taksitDetaylari];
                              updatedTaksitler[index].tutar = parseFloat(e.target.value) || 0;
                              setTaksitDetaylari(updatedTaksitler);
                            }}
                            InputProps={{ inputProps: { min: 0, step: "0.01" } }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={taksit.borcTuru}
                              onChange={(e) => {
                                const updatedTaksitler = [...taksitDetaylari];
                                updatedTaksitler[index].borcTuru = e.target.value;
                                setTaksitDetaylari(updatedTaksitler);
                              }}
                            >
                              {odemeTurleri.map((tur) => (
                                <MenuItem key={tur} value={tur}>{tur}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                Taksit detayları otomatik olarak hesaplanacaktır.
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={resetTaksitPlanForm}
                disabled={createTaksitPlanMutation.isPending}
              >
                Temizle
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateTaksitPlan}
                disabled={
                  listeFiyati <= 0 ||
                  taksitSayisi <= 0 ||
                  taksitlendirilecekMiktar < 0 ||
                  createTaksitPlanMutation.isPending
                }
              >
                {createTaksitPlanMutation.isPending ? <CircularProgress size={24} /> : 'Taksit Planı Oluştur'}
              </Button>
            </Box>

            {createTaksitPlanMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {(createTaksitPlanMutation.error as Error).message}
              </Alert>
            )}

            {createTaksitPlanMutation.isSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Taksit planı başarıyla oluşturuldu.
              </Alert>
            )}
          </Paper>
        )}

        {/* Taksit Planı Listesi */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert
            severity="info"
            sx={{ mt: 2 }}
          >
            Bu öğrenciye ait taksit planı bulunamadı. {process.env.NODE_ENV === 'development' && `(${(error as Error).message})`}
          </Alert>
        ) : taksitPlan ? (
          <>
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">Liste Fiyatı</Typography>
                  <Typography variant="body1">{taksitPlan.listeFiyati.toFixed(2)} ₺</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">İndirim Tutarı</Typography>
                  <Typography variant="body1">{taksitPlan.indirimTutari.toFixed(2)} ₺</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">Net Ücret</Typography>
                  <Typography variant="body1">{taksitPlan.netUcret.toFixed(2)} ₺</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">Peşinat Tutarı</Typography>
                  <Typography variant="body1">{taksitPlan.pesinatTutari.toFixed(2)} ₺</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">Taksitlendirilecek Miktar</Typography>
                  <Typography variant="body1">{taksitPlan.taksitlendirilecekMiktar.toFixed(2)} ₺</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">Taksit Sayısı</Typography>
                  <Typography variant="body1">{taksitPlan.taksitSayisi}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">Ödenen Tutar</Typography>
                  <Typography variant="body1">{taksitPlan.odenenTutar.toFixed(2)} ₺</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">Kalan Tutar</Typography>
                  <Typography variant="body1">{taksitPlan.kalanTutar.toFixed(2)} ₺</Typography>
                </Grid>
              </Grid>
            </Box>

            {taksitPlan.indirimDetaylari.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>İndirimler</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>İndirim Türü</TableCell>
                        <TableCell>Oran (%)</TableCell>
                        <TableCell>Tutar (₺)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {taksitPlan.indirimDetaylari.map((indirim, index) => (
                        <TableRow key={index}>
                          <TableCell>{indirim.indirimTuru}</TableCell>
                          <TableCell>{indirim.oran.toFixed(2)}%</TableCell>
                          <TableCell>{indirim.tutar.toFixed(2)} ₺</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            <Typography variant="subtitle2" gutterBottom>Taksitler</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Taksit No</TableCell>
                    <TableCell>Vade Tarihi</TableCell>
                    <TableCell>Tutar (₺)</TableCell>
                    <TableCell>Ödenen Tutar (₺)</TableCell>
                    <TableCell>Ödeme Türü</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell>İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {taksitPlan.taksitDetaylari.map((taksit, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: taksit.odendiMi ? '#e8f5e9' : 'inherit'
                      }}
                    >
                      <TableCell>{taksit.taksitNo}. Taksit</TableCell>
                      <TableCell>{taksit.vadeTarihi}</TableCell>
                      <TableCell>{taksit.tutar.toFixed(2)} ₺</TableCell>
                      <TableCell>{taksit.odenenTutar.toFixed(2)} ₺</TableCell>
                      <TableCell>{taksit.borcTuru}</TableCell>
                      <TableCell>
                        {taksit.odendiMi ? (
                          <Typography variant="body2" color="success.main">Ödendi</Typography>
                        ) : (
                          <Typography variant="body2" color="error.main">Ödenmedi</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {!taksit.odendiMi && (
                          <Tooltip title="Ödeme Yap">
                            <IconButton
                              color="primary"
                              onClick={() => handleSelectTaksit(taksit)}
                              size="small"
                            >
                              <PaymentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Taksit Ödeme Formu */}
            {showOdemeForm && selectedTaksit && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle1" gutterBottom>
                  {selectedTaksit.taksitNo}. Taksit Ödemesi
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Tutar (₺)"
                      type="number"
                      fullWidth
                      value={odemeTutari}
                      onChange={(e) => setOdemeTutari(parseFloat(e.target.value) || 0)}
                      size="small"
                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: selectedTaksit.tutar - selectedTaksit.odenenTutar,
                          step: "0.01"
                        }
                      }}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Ödeme Türü</InputLabel>
                      <Select
                        value={odemeTuru}
                        onChange={(e) => setOdemeTuru(e.target.value)}
                        label="Ödeme Türü"
                      >
                        {odemeTurleri.map((tur) => (
                          <MenuItem key={tur} value={tur}>{tur}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Açıklama"
                      fullWidth
                      value={odemeAciklama}
                      onChange={(e) => setOdemeAciklama(e.target.value)}
                      size="small"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      resetOdemeForm();
                      setShowOdemeForm(false);
                    }}
                    disabled={odeTaksitMutation.isPending}
                  >
                    İptal
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOdeTaksit}
                    disabled={
                      odemeTutari <= 0 ||
                      odemeTutari > (selectedTaksit.tutar - selectedTaksit.odenenTutar) ||
                      odeTaksitMutation.isPending
                    }
                  >
                    {odeTaksitMutation.isPending ? <CircularProgress size={24} /> : 'Ödeme Yap'}
                  </Button>
                </Box>

                {odeTaksitMutation.isError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {(odeTaksitMutation.error as Error).message}
                  </Alert>
                )}

                {odeTaksitMutation.isSuccess && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Ödeme başarıyla kaydedildi.
                  </Alert>
                )}
              </Paper>
            )}
          </>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaksitPlanDialogModal;